import React, { useState, useMemo, useEffect } from "react";
import { Table, Tag, Tooltip, Input, Row, Col, Pagination } from "antd";
import { CheckCircleOutlined, MinusOutlined, SearchOutlined } from "@ant-design/icons";
import { BulkDocsInfoResult, DocInfo } from "../../../interfaces/account.interface";

interface Props {
  data: BulkDocsInfoResult;
}

const DOC_KEYS = ["PO", "SO", "TO", "GRN", "Invoice", "EWayBill", "ServiceInvoice", "Package", "Shipment", "MarkDelivered"];
const PO_PAGE_SIZE = 15;

const DocCell: React.FC<{ info: DocInfo | undefined }> = ({ info }) => {
  if (!info || !info.exists) return <MinusOutlined style={{ color: "#ccc" }} />;
  return (
    <Tooltip title={info.number || info.id || "exists"}>
      <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
    </Tooltip>
  );
};

interface FlatRow {
  key: string;
  po_number: string;
  leg: string;
  leg_count: number;
  leg_status: string | null;
  docs: Record<string, DocInfo>;
  rowSpan: number; // first row of a PO group: number of legs; merged rows: 0
}

const ExistingDocsPanel: React.FC<Props> = ({ data }) => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    for (const po of data.results) {
      const legKeys = Object.keys(po.legs || {}).sort(
        (a, b) => parseInt(a.replace("leg_", "")) - parseInt(b.replace("leg_", ""))
      );
      if (legKeys.length === 0) {
        rows.push({
          key: po.po_number,
          po_number: po.po_number,
          leg: "1",
          leg_count: po.leg_count,
          leg_status: po.leg_status,
          docs: {},
          rowSpan: 1,
        });
      } else {
        legKeys.forEach((legKey, i) => {
          const legNum = legKey.replace("leg_", "");
          rows.push({
            key: `${po.po_number}_${legKey}`,
            po_number: po.po_number,
            leg: legNum,
            leg_count: po.leg_count,
            leg_status: po.leg_status,
            docs: po.legs[legKey] || {},
            rowSpan: i === 0 ? legKeys.length : 0,
          });
        });
      }
    }
    return rows;
  }, [data.results]);

  const filteredRows = useMemo(() => {
    if (!searchText) return flatRows;
    return flatRows.filter((r) =>
      r.po_number.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [flatRows, searchText]);

  // Group filtered rows by PO so pagination never splits a PO across pages.
  const poGroups: FlatRow[][] = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, FlatRow[]>();
    for (const r of filteredRows) {
      if (!map.has(r.po_number)) {
        map.set(r.po_number, []);
        order.push(r.po_number);
      }
      map.get(r.po_number)!.push(r);
    }
    return order.map((po) => map.get(po)!);
  }, [filteredRows]);

  const pagedRows: FlatRow[] = useMemo(() => {
    const start = (currentPage - 1) * PO_PAGE_SIZE;
    return poGroups.slice(start, start + PO_PAGE_SIZE).flat();
  }, [poGroups, currentPage]);

  const columns = [
    {
      title: "PO Number",
      dataIndex: "po_number",
      key: "po_number",
      fixed: "left" as const,
      width: 160,
      render: (pn: string, record: FlatRow) => {
        if (record.rowSpan === 0) return { children: null, props: { rowSpan: 0 } };
        return {
          children: (
            <span style={{ fontWeight: 600 }}>
              {pn}
              {record.leg_count > 1 && (
                <Tag color="blue" style={{ marginLeft: 6, fontSize: 10 }}>
                  {record.leg_count} legs
                </Tag>
              )}
            </span>
          ),
          props: { rowSpan: record.rowSpan },
        };
      },
    },
    {
      title: "Leg",
      dataIndex: "leg",
      key: "leg",
      width: 60,
      align: "center" as const,
      render: (leg: string) => leg,
    },
    {
      title: "Status",
      dataIndex: "leg_status",
      key: "leg_status",
      width: 100,
      render: (status: string | null) => {
        if (!status) return <MinusOutlined style={{ color: "#ccc" }} />;
        const color = status === "delivered" ? "green" : status === "dispatched" ? "blue" : "default";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    ...DOC_KEYS.map((key) => ({
      title: key,
      key,
      width: 90,
      align: "center" as const,
      render: (_: any, record: FlatRow) => <DocCell info={record.docs[key]} />,
    })),
  ];

  return (
    <>
      <Row style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Input
            placeholder="Search PO numbers..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
          />
        </Col>
      </Row>

      {searchText && (
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Tag color="blue">
              Found {new Set(filteredRows.map((r) => r.po_number)).size} of {data.results.length} POs
            </Tag>
          </Col>
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={pagedRows}
        pagination={false}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
      />

      {poGroups.length > PO_PAGE_SIZE && (
        <Row justify="end" style={{ marginTop: 16 }}>
          <Pagination
            current={currentPage}
            total={poGroups.length}
            pageSize={PO_PAGE_SIZE}
            onChange={setCurrentPage}
            showSizeChanger={false}
            showTotal={(t) => `${t} POs`}
          />
        </Row>
      )}
    </>
  );
};

export default ExistingDocsPanel;
